import { useRef } from 'react';
import { Icon, IconButton, icons } from '@agentisai/design-system';
import { UPLOAD_ACCEPT } from '@agentisai/shared-types';

export interface AttachFileButtonProps {
  /** One or more files were picked. Validation is the caller's job — this
   * relays the raw `File`s, exactly as `NewFileAction` does for the rail. */
  readonly onFilesPicked: (files: File[]) => void;
  /** Disable while a flush is in flight (the staged list is being uploaded). */
  readonly disabled?: boolean;
}

/**
 * The composer paperclip — a hidden multi-select native file input behind an
 * IconButton. Shared by the create panel and the conversation composer so both
 * accept the same types and the same multi-pick gesture.
 *
 * `accept` is a picker **hint** only; `validateUpload` is the real gate and runs
 * on every file regardless of how it arrived (a user can always choose "All
 * files" in the OS dialog). The input value is cleared on each change so
 * re-picking the same filename fires again.
 *
 * `disabled` is set on the INPUT as well as the button. The create panel's
 * "Upload an example" chip reaches this input directly (`querySelector` off the
 * shared composer ref) rather than through the paperclip, so a `disabled` that
 * lived only on the button let that chip open the picker mid-flush. Gating the
 * input is the one place every caller — present and future — has to go through.
 */
export function AttachFileButton({ onFilesPicked, disabled = false }: AttachFileButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={UPLOAD_ACCEPT}
        multiple
        hidden
        disabled={disabled}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length > 0) onFilesPicked(files);
        }}
      />
      <IconButton
        aria-label="Attach files"
        size="sm"
        variant="ghost"
        disabled={disabled}
        icon={<Icon as={icons.Paperclip} size={14} />}
        onClick={() => inputRef.current?.click()}
      />
    </>
  );
}
AttachFileButton.displayName = 'AttachFileButton';
